import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { getAuthHeaders } from "@/lib/queryClient";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { isAdminUser } from "@chefaiq/shared/lib/admin";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WorkspaceGeneralTab } from "@/components/workspace/WorkspaceGeneralTab";
import { WorkspaceRegionalPreferencesTab } from "@/components/workspace/WorkspaceRegionalPreferencesTab";
import { AIChatToggleTab } from "@/components/workspace/AIChatToggleTab";
import { Globe, Bot, Image, X, Upload, Shield, Building2, Settings } from "lucide-react";
import { MemberPermissionsEditor } from "@/components/workspace/MemberPermissionsEditor";
import { DepartmentsManager } from "@/components/workspace/DepartmentsManager";
import { showSuccess, showError } from "@/lib/toastHelpers";

export default function WorkspaceSettings() {
  const { currentWorkspace, user } = useWorkspace() as any;
  const [activeTab, setActiveTab] = useState("general");

  const isAdmin = isAdminUser(user);

  if (!currentWorkspace) {
    return (
      <div className="container mx-auto py-6 max-w-4xl">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              No workspace selected.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 max-w-4xl space-y-6">
      {/* Desktop: Full header */}
      <div className="hidden md:block">
        <h1 className="text-xl sm:text-3xl font-bold">Workspace Settings</h1>
        <p className="text-muted-foreground">
          Settings apply to <strong>{currentWorkspace.name}</strong> workspace only
        </p>
      </div>

      {/* Mobile: Compact header */}
      <div className="md:hidden">
        <h1 className="text-xl font-bold">Settings</h1>
        <p className="text-sm text-muted-foreground">{currentWorkspace.name}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Desktop: Horizontal tabs */}
        <TabsList className="hidden md:inline-flex">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="regional" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Regional Preferences
          </TabsTrigger>
          <TabsTrigger value="logo" className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            Logo
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="ai-chat" className="flex items-center gap-2">
              <Bot className="w-4 h-4" />
              AI Chat
            </TabsTrigger>
          )}
          <TabsTrigger value="permissions" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Team & Permissions
          </TabsTrigger>
          <TabsTrigger value="departments" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Departments
          </TabsTrigger>
        </TabsList>

        {/* Mobile: Icon grid */}
        <div className={`grid ${isAdmin ? 'grid-cols-2' : 'grid-cols-1'} gap-2 md:hidden`}>
          <button
            onClick={() => setActiveTab("regional")}
            className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
              activeTab === "regional"
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background hover:bg-accent"
            }`}
          >
            <Globe className="h-5 w-5" />
            <span className="text-xs font-medium text-center">Regional</span>
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab("ai-chat")}
              className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
                activeTab === "ai-chat"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background hover:bg-accent"
              }`}
            >
              <Bot className="h-5 w-5" />
              <span className="text-xs font-medium text-center">AI Chat</span>
            </button>
          )}
        </div>

        <TabsContent value="general" className="mt-6">
          <WorkspaceGeneralTab workspace={currentWorkspace} />
        </TabsContent>

        <TabsContent value="regional" className="mt-6">
          <WorkspaceRegionalPreferencesTab 
            workspaceId={currentWorkspace._id}
            isTeamWorkspace={currentWorkspace.type === 'team'}
            isOwner={true}
          />
        </TabsContent>

        <TabsContent value="logo" className="mt-6">
          <WorkspaceLogoTab workspace={currentWorkspace} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="ai-chat" className="mt-6">
            <AIChatToggleTab
              workspaceId={currentWorkspace._id}
              initialEnabled={currentWorkspace.aiChatEnabled ?? true}
            />
          </TabsContent>
        )}
      
        <TabsContent value="permissions" className="mt-6">
          <MemberPermissionsEditor />
        </TabsContent>

        <TabsContent value="departments" className="mt-6">
          <DepartmentsManager />
        </TabsContent>
</Tabs>
    </div>
  );
}

function WorkspaceLogoTab({ workspace }: { workspace: any }) {
  const [logo, setLogo] = useState<string>(workspace.logo || '');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await fetch(`/api/workspaces/logo`, {
        method: 'POST',
        headers: (getAuthHeaders as any)(workspace._id),
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      setLogo(url);
      queryClient.invalidateQueries({ queryKey: ['/api/workspaces'] });
      showSuccess('Workspace logo updated');
    } catch { showError('Failed to upload logo'); }
    finally { setUploading(false); }
  };

  const handleRemove = async () => {
    try {
      const res = await fetch(`/api/workspaces/logo`, {
        method: 'DELETE',
        headers: (getAuthHeaders as any)(workspace._id),
      });
      if (!res.ok) throw new Error('Delete failed');
      setLogo('');
      queryClient.invalidateQueries({ queryKey: ['/api/workspaces'] });
      showSuccess('Workspace logo removed');
    } catch { showError('Failed to remove logo'); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workspace Logo</CardTitle>
        <CardDescription>This logo appears in the sidebar workspace switcher.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-6">
          <div className="relative">
            {logo ? (
              <>
                <img src={logo} alt="Workspace logo" className="w-20 h-20 rounded-lg object-cover border" />
                <Button type="button" variant="destructive" size="icon"
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full" onClick={handleRemove}>
                  <X className="h-3 w-3" />
                </Button>
              </>
            ) : (
              <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center border">
                <Image className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" className="hidden" accept="image/*,.heic,.heif"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Logo'}
            </Button>
            <p className="text-xs text-muted-foreground mt-1">JPG, PNG or HEIC. Max 2MB.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
